import { CAR_QUOTE_STEP_KEYS } from "@/D2C/features/car-quote/common/static";
import { saveCarQuoteStepFor } from "@/D2C/features/car-quote/redux/car-quote/carQuoteSlice";
import { CarUploadedDocumentMap } from "@/D2C/features/car-quote/redux/type";
import { updateCarSessionStep } from "@/D2C/features/car-quote/services/service";
import {
  buildCarAttachmentsPayload,
  buildCarBindPayload,
  buildCarUploadDocPayload,
} from "@/D2C/features/car-quote/utils/carQuotePayloadsUtils";
import { HEALTH_QUOTE_STEP_KEYS } from "@/D2C/features/health-quote/common/static";
import { saveHealthQuoteStepFor } from "@/D2C/features/health-quote/redux/health-quote/healthQuoteSlice";
import { HealthUploadedDocumentMap } from "@/D2C/features/health-quote/redux/type";
import { updateHealthSessionStep } from "@/D2C/features/health-quote/services/service";
import {
  buildHealthAttachmentsPayload,
  buildHealthBindPayload,
  buildHealthUploadDocPayload,
} from "@/D2C/features/health-quote/utils/healthQuotePayloadsUtils";
import { LIFE_QUOTE_STEP_KEYS } from "@/D2C/features/life-quote/common/static";
import {
  // saveLifeAdditionalInfo,
  saveLifeQuoteStepFor,
} from "@/D2C/features/life-quote/redux/lifeQuoteSlice";
import { updateLifeSessionStep } from "@/D2C/features/life-quote/services/service";
import {
  buildLifeBindPayload,
  buildLifeUploadDocPayload,
} from "@/D2C/features/life-quote/utils/lifeQuotePayloadsUtils";
import { TRAVEL_QUOTE_STEP_KEYS } from "@/D2C/features/travel-quote/common/static";
import { saveTravelQuoteStepFor } from "@/D2C/features/travel-quote/redux/travel-quote/travelQuoteSlice";
import { TravelUploadedDocumentMap } from "@/D2C/features/travel-quote/redux/type";
import { updateTravelSessionStep } from "@/D2C/features/travel-quote/services/service";
import {
  buildTravelAttachmentsPayload,
  buildTravelBindPayload,
  buildTravelUploadDocPayload,
} from "@/D2C/features/travel-quote/utils/travelQuotePayloadsUtils";
import { LOB_IDS } from "@/shared/common/static";
import { SESSION_STEP_KEYS } from "@/shared/constants/sessionStepKeys";
import { showToast } from "@/shared/lib/toast";
import type { AppDispatch } from "@/shared/redux/store/store";
import { bindquote } from "@/shared/services/bindquote";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import type { useQuoteRefs } from "./useQuoteRefs";

type Refs = ReturnType<typeof useQuoteRefs>;

type Options = {
  lobId: number;
  refs: Refs;
  selectedQuote: any;
  selectedAddOns: string[];
  clientId: number | string;
  sessionId: number | string;
  travelUploadedDocuments: TravelUploadedDocumentMap;
  carUploadedDocuments: CarUploadedDocumentMap;
  healthUploadedDocuments: HealthUploadedDocumentMap;
  travelStructuredOcrData: any;
  carStructuredOcrData: any;
  healthStructuredOcrData: any;
};

export const usePostQuote = (opts: Options) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isPostSubmitting, setIsPostSubmitting] = useState(false);

  const validateContext = useCallback(() => {
    const { selectedQuote, clientId, sessionId } = opts;
    if (!selectedQuote || !clientId || !sessionId) {
      showToast("Session not found. Please get quotes again.", "error");
      return null;
    }

    return {
      selectedQuote,
      clientId,
      sessionId,
      lobId: opts.lobId,
      refs: opts.refs,
    };
  }, [opts]);

  const handleSubmitUploadOnly = useCallback(
    async (showSuccessToast = true) => {
      const context = validateContext();
      if (!context) return false;

      const { lobId, refs, selectedQuote, clientId, sessionId } = context;

      try {
        setIsPostSubmitting(true);

        if (lobId === LOB_IDS.TRAVEL) {
          const isUploadValid = await refs.travelDocumentUploadRef.current?.submit();
          if (!isUploadValid) return false;

          const uploadValues = refs.travelDocumentUploadRef.current!.getValues();
          const additionalValues = refs.travelAdditionalInfoRef.current!.getValues();

          dispatch(saveTravelQuoteStepFor(TRAVEL_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD, uploadValues));

          await bindquote(
            buildTravelUploadDocPayload({
              selectedQuote,
              clientId,
              sessionId,
              travelDetails: refs.travelDetailsRef.current?.getValues() as any,
              additionalInfo: additionalValues,
              documents: opts.travelUploadedDocuments,
              lob_id: LOB_IDS.TRAVEL,
            })
          );

          await updateTravelSessionStep({
            step: SESSION_STEP_KEYS.DOCUMENT_UPLOAD,
            payload: {
              document_upload: uploadValues,
              attachments: buildTravelAttachmentsPayload(opts.travelUploadedDocuments),
            },
            clientId,
            sessionId,
            lobId: LOB_IDS.TRAVEL,
          });

          if (showSuccessToast) {
            showToast("Travel document upload saved.", "success");
          }

          return true;
        }

        if (lobId === LOB_IDS.LIFE) {
          const uploadValues = refs.travelDocumentUploadRef.current!.getValues();
          const lifePersonalDetails = refs.lifePersonalRef.current?.getValues() as any;
          const lifeTermDetails = refs.lifePlanDetailsRef.current?.getValues() as any;
          const additionalValues = {};

          dispatch(saveLifeQuoteStepFor(LIFE_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD, uploadValues));

          await bindquote(
            buildLifeUploadDocPayload({
              quote: selectedQuote,
              clientId,
              sessionId,
              personalDetails: lifePersonalDetails,
              termDetails: lifeTermDetails,
              additionalInfo: additionalValues,
              documents: opts.travelUploadedDocuments,
              lob_id: LOB_IDS.LIFE,
            })
          );

          await updateLifeSessionStep({
            // Life has no additional-details step, so upload confirmation must advance the
            // session past step_6 directly, otherwise isPostDataReady never becomes true on resume.
            step: SESSION_STEP_KEYS.ADDITIONAL_INFO,
            payload: {
              document_upload: uploadValues,
              attachments: buildTravelAttachmentsPayload(opts.travelUploadedDocuments),
            },
            clientId,
            sessionId,
            lobId: LOB_IDS.LIFE,
          });

          if (showSuccessToast) {
            showToast("Life document upload saved.", "success");
          }

          return true;
        }

        if (lobId === LOB_IDS.MOTOR) {
          const isUploadValid = await refs.carDocumentUploadRef.current?.submit();
          if (!isUploadValid) return false;

          const uploadValues = refs.carDocumentUploadRef.current!.getValues();
          const additionalValues = refs.carAdditionalInfoRef.current!.getValues();

          dispatch(saveCarQuoteStepFor(CAR_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD, uploadValues));

          await bindquote(
            buildCarUploadDocPayload({
              selectedQuote,
              clientId,
              sessionId,
              selectedAddOns: opts.selectedAddOns,
              personalDetails: refs.carPersonalRef.current?.getValues() as any,
              driverDetails: refs.carDriverRef.current?.getValues() as any,
              additionalInfo: additionalValues,
              documents: opts.carUploadedDocuments,
              lob_id: LOB_IDS.MOTOR,
            })
          );

          await updateCarSessionStep({
            step: SESSION_STEP_KEYS.DOCUMENT_UPLOAD,
            payload: {
              document_upload: uploadValues,
              attachments: buildCarAttachmentsPayload(opts.carUploadedDocuments),
            },
            clientId,
            sessionId,
            lobId: LOB_IDS.MOTOR,
          });

          if (showSuccessToast) {
            showToast("Motor document upload saved.", "success");
          }

          return true;
        }

        const isUploadValid = await refs.healthDocumentUploadRef.current?.submit();
        if (!isUploadValid) return false;

        const uploadValues = refs.healthDocumentUploadRef.current!.getValues();
        const additionalValues = refs.healthAdditionalInfoRef.current!.getValues();

        dispatch(saveHealthQuoteStepFor(HEALTH_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD, uploadValues));

        await bindquote(
          buildHealthUploadDocPayload({
            selectedQuote,
            clientId,
            sessionId,
            personalDetails: refs.healthPersonalRef.current?.getValues() as any,
            healthDetails: refs.healthDetailsRef.current?.getValues() as any,
            additionalInfo: additionalValues,
            documents: opts.healthUploadedDocuments,
            lob_id: LOB_IDS.HEALTH,
          })
        );

        await updateHealthSessionStep({
          step: SESSION_STEP_KEYS.DOCUMENT_UPLOAD,
          payload: {
            document_upload: uploadValues,
            attachments: buildHealthAttachmentsPayload(opts.healthUploadedDocuments),
          },
          clientId,
          sessionId,
          lobId: LOB_IDS.HEALTH,
        });

        if (showSuccessToast) {
          showToast("Health document upload saved.", "success");
        }

        return true;
      } catch (error: any) {
        showToast(error?.message || "Document upload save failed.", "error");
        return false;
      } finally {
        setIsPostSubmitting(false);
      }
    },
    [dispatch, opts, validateContext]
  );

  const handleSubmitAdditionalOnly = useCallback(
    async (showSuccessToast = true) => {
      const context = validateContext();
      if (!context) return false;

      const { lobId, refs, selectedQuote, clientId, sessionId } = context;

      try {
        setIsPostSubmitting(true);

        if (lobId === LOB_IDS.TRAVEL) {
          const isAdditionalValid = await refs.travelAdditionalInfoRef.current?.submit();
          if (!isAdditionalValid) return false;

          const additionalValues = refs.travelAdditionalInfoRef.current!.getValues();

          dispatch(
            saveTravelQuoteStepFor(TRAVEL_QUOTE_STEP_KEYS.ADDITIONAL_INFO, additionalValues)
          );

          await bindquote(
            buildTravelBindPayload({
              selectedQuote,
              clientId,
              sessionId,
              travelDetails: refs.travelDetailsRef.current?.getValues() as any,
              additionalInfo: additionalValues,
            })
          );

          await updateTravelSessionStep({
            step: SESSION_STEP_KEYS.ADDITIONAL_INFO,
            payload: { additional_info: additionalValues },
            clientId,
            sessionId,
            lobId: LOB_IDS.TRAVEL,
          });

          if (showSuccessToast) {
            showToast("Travel additional details saved.", "success");
          }

          return true;
        }

        if (lobId === LOB_IDS.LIFE) {
          if (!refs.travelAdditionalInfoRef.current) {
            return true;
          }

          const isAdditionalValid = await refs.travelAdditionalInfoRef.current?.submit();
          if (!isAdditionalValid) return false;

          const additionalValues = refs.travelAdditionalInfoRef.current!.getValues();
          const lifePersonalDetails = refs.lifePersonalRef.current?.getValues() as any;
          const lifeTermDetails = refs.lifePlanDetailsRef.current?.getValues() as any;

          // dispatch(saveLifeAdditionalInfo(additionalValues));

          await bindquote(
            buildLifeBindPayload({
              quote: selectedQuote,
              clientId,
              sessionId,
              personalDetails: lifePersonalDetails,
              termDetails: lifeTermDetails,
              additionalInfo: additionalValues,
            })
          );

          await updateLifeSessionStep({
            step: SESSION_STEP_KEYS.ADDITIONAL_INFO,
            payload: { additional_info: additionalValues },
            clientId,
            sessionId,
            lobId: LOB_IDS.LIFE,
          });

          if (showSuccessToast) {
            showToast("Life additional details saved.", "success");
          }

          return true;
        }

        if (lobId === LOB_IDS.MOTOR) {
          const isAdditionalValid = await refs.carAdditionalInfoRef.current?.submit();
          if (!isAdditionalValid) return false;

          const additionalValues = refs.carAdditionalInfoRef.current!.getValues();

          dispatch(saveCarQuoteStepFor(CAR_QUOTE_STEP_KEYS.ADDITIONAL_INFO, additionalValues));

          await bindquote(
            buildCarBindPayload({
              selectedQuote,
              clientId,
              sessionId,
              selectedAddOns: opts.selectedAddOns,
              personalDetails: refs.carPersonalRef.current?.getValues() as any,
              driverDetails: refs.carDriverRef.current?.getValues() as any,
              additionalInfo: additionalValues,
              lob_id: LOB_IDS.MOTOR,
            })
          );

          await updateCarSessionStep({
            step: SESSION_STEP_KEYS.ADDITIONAL_INFO,
            payload: { additional_info: additionalValues },
            clientId,
            sessionId,
            lobId: LOB_IDS.MOTOR,
          });

          if (showSuccessToast) {
            showToast("Motor additional details saved.", "success");
          }

          return true;
        }

        const isAdditionalValid = await refs.healthAdditionalInfoRef.current?.submit();
        if (!isAdditionalValid) return false;

        const additionalValues = refs.healthAdditionalInfoRef.current!.getValues();

        dispatch(saveHealthQuoteStepFor(HEALTH_QUOTE_STEP_KEYS.ADDITIONAL_INFO, additionalValues));

        await bindquote(
          buildHealthBindPayload({
            selectedQuote,
            clientId,
            sessionId,
            personalDetails: refs.healthPersonalRef.current?.getValues() as any,
            healthDetails: refs.healthDetailsRef.current?.getValues() as any,
            additionalInfo: additionalValues,
            lob_id: LOB_IDS.HEALTH,
          })
        );

        await updateHealthSessionStep({
          step: SESSION_STEP_KEYS.ADDITIONAL_INFO,
          payload: { additional_info: additionalValues },
          clientId,
          sessionId,
          lobId: LOB_IDS.HEALTH,
        });

        if (showSuccessToast) {
          showToast("Health additional details saved.", "success");
        }

        return true;
      } catch (error: any) {
        showToast(error?.message || "Additional details save failed.", "error");
        return false;
      } finally {
        setIsPostSubmitting(false);
      }
    },
    [dispatch, opts, validateContext]
  );

  const handleSubmitPostQuote = useCallback(async () => {
    const uploadSaved = await handleSubmitUploadOnly(false);
    if (!uploadSaved) return false;

    const additionalSaved = await handleSubmitAdditionalOnly(false);
    if (!additionalSaved) return false;

    if (opts.lobId === LOB_IDS.TRAVEL) {
      showToast("Travel post-quote details submitted.", "success");
    } else if (opts.lobId === LOB_IDS.LIFE) {
      showToast("Life post-quote details submitted.", "success");
    } else if (opts.lobId === LOB_IDS.MOTOR) {
      showToast("Motor post-quote details submitted.", "success");
    } else {
      showToast("Health post-quote details submitted.", "success");
    }

    return true;
  }, [handleSubmitAdditionalOnly, handleSubmitUploadOnly, opts.lobId]);

  return {
    isPostSubmitting,
    handleSubmitPostQuote,
    handleSubmitUploadOnly,
    handleSubmitAdditionalOnly,
  };
};
